import React from 'react';
import {ConfirmationModal} from '@albert-io/atomic/molecules/ConfirmationModal/ConfirmationModal.react';
import {Anchor} from '@albert-io/atomic';

export type ArchivedQuestionModalType =
  | 'reassign'
  | 'save-as-draft'
  | 'save-to-folder'
  | 'duplicate-folder'
  | 'folder-assign-all';

interface ArchivedQuestionModalProps {
  isOpen: boolean;
  archivedQuestionCount: number;
  type: ArchivedQuestionModalType;
  onConfirm: () => void;
  onCancel: () => void;
}

const defaultBodyText = (
  <div>
    Learn more about why we archive questions{' '}
    <Anchor
      href='https://help.albert.io/en/articles/12754786-what-are-archived-questions'
      target='_blank'
    >
      here
    </Anchor>
    .
  </div>
);

const getModalContent = (type: ArchivedQuestionModalType, count: number) => {
  const pluralizedQuestions = count === 1 ? 'question' : 'questions';

  switch (type) {
    case 'reassign':
      return {
        title: `Your new assignment will not include ${count} ${pluralizedQuestions} that have been archived`,
        body: defaultBodyText
      };
    case 'save-as-draft':
      return {
        title: `Your new draft will not include ${count} ${pluralizedQuestions} that have been archived`,
        body: defaultBodyText
      };
    case 'save-to-folder':
      return {
        title: `Your folder will not include ${count} ${pluralizedQuestions} that have been archived`,
        body: defaultBodyText
      };
    case 'duplicate-folder':
      return {
        title: `Your new folder will not include ${count} ${pluralizedQuestions} that have been archived`,
        body: defaultBodyText
      };
    case 'folder-assign-all':
      return {
        title: 'Remove archived questions to assign',
        body: `This folder contains ${count} archived ${pluralizedQuestions} that cannot be assigned. Would you like to assign without the archived questions?`
      };
    default:
      return {
        title: `Your assignment will not include ${count} ${pluralizedQuestions} that have been archived`,
        body: defaultBodyText
      };
  }
};

export const ArchivedQuestionModal: React.FC<ArchivedQuestionModalProps> = ({
  isOpen,
  archivedQuestionCount,
  type,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) {
    return null;
  }

  const {title, body} = getModalContent(type, archivedQuestionCount);

  const handleCancel = () => {
    onCancel();
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <ConfirmationModal
      badgeColor='warning'
      badgeIcon={['fal', 'exclamation-triangle']}
      title={title}
      body={body}
      confirmText='Continue'
      cancelText='Cancel'
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmBtnVariant='outlined'
    />
  );
};

export default ArchivedQuestionModal;
